/* Meta (Facebook) Pixel — consent-gated, advertising/marketing category.
   Loads ONLY after the visitor accepts cookies (the same single choice that
   enables GA4 — see src/components/CookieConsent.jsx). Mirrors the GA4 pattern:
     - fbevents.js is NOT in index.html and never loads before consent.
     - There is deliberately NO <noscript> fallback image (it would bypass consent).
     - Automatic configuration + Advanced Matching are disabled
       (fbq('set','autoConfig',false,…) and no user-data object on init),
       so NO personal/financial data is ever sent to Meta.
   Events used: standard PageView (route-aware, deduped) and standard Lead
   (fired once per confirmed partner delivery, keyed by the opaque reference).
   Conversions API (server-side) is intentionally NOT implemented. */

const PIXEL_ID = "1102887875460052"; // public pixel/dataset id — not a secret
const SCRIPT_ID = "meta-pixel-script";
const LEAD_PREFIX = "meta-lead-fired:";

let initialized = false;
let consentGranted = false;
let lastPageViewKey = null;

const makeQueue = () => {
  if (window.fbq) return;
  const fbq = function (...args) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  };
  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
};

const hasConsent = () =>
  consentGranted && initialized && typeof window !== "undefined" && typeof window.fbq === "function";

/* Called only after affirmative cookie consent. Injects fbevents.js and inits
   the pixel in manual-only mode (no auto-config, no Advanced Matching). */
export const enableMetaPixel = () => {
  if (typeof window === "undefined" || initialized) return;
  consentGranted = true;
  makeQueue();
  // autoConfig MUST be set before init — disables automatic button/metadata collection.
  window.fbq("set", "autoConfig", false, PIXEL_ID);
  // No second argument object → no manual Advanced Matching, no PII.
  window.fbq("init", PIXEL_ID);

  if (!document.getElementById(SCRIPT_ID)) {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
  }
  initialized = true;
  lastPageViewKey = null; // ensure the next (initial) PageView fires
};

/* Consent withdrawal: stop future fires and remove the script. Cannot retract
   events already sent — it prevents all subsequent tracking. */
export const revokeMetaPixel = () => {
  consentGranted = false;
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("consent", "revoke");
  }
  const s = typeof document !== "undefined" ? document.getElementById(SCRIPT_ID) : null;
  if (s) s.remove();
  initialized = false;
  lastPageViewKey = null;
};

/* Standard PageView — deduped on consecutive identical keys so the initial
   route is never double-counted (loader + router both route through here). */
export const trackMetaPageView = (key) => {
  if (!hasConsent()) return;
  const k = key || (typeof window !== "undefined" ? window.location.pathname : "");
  if (k === lastPageViewKey) return;
  lastPageViewKey = k;
  window.fbq("track", "PageView");
};

/* Standard Lead — opaque eventID only, empty event data, NEVER any PII. */
export const trackMetaLead = (eventID) => {
  if (!hasConsent() || !eventID) return;
  window.fbq("track", "Lead", {}, { eventID });
};

/* Fire a confirmed Lead at most once per delivery, keyed by the opaque partner
   reference. Guards against refreshes, retries and repeated success callbacks.
   Returns true only when a new Lead was actually fired. */
export const fireMetaLeadOnce = (reference) => {
  if (typeof window === "undefined" || !reference || !hasConsent()) return false;
  const key = LEAD_PREFIX + reference;
  try {
    if (localStorage.getItem(key) === "1") return false;
    localStorage.setItem(key, "1"); // reserve before firing
  } catch {
    /* storage unavailable — still fire once for this page load */
  }
  trackMetaLead(reference);
  return true;
};
